import { lazy, StrictMode, Suspense, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import '@fontsource-variable/inter';
import '@fontsource/jetbrains-mono/400.css';
import '@fontsource/jetbrains-mono/500.css';
import './styles/global.css';
import { useSession } from './lib/session';
import { useLang } from './lib/i18n';
import { applyMode, applyTheme, savedMode, savedTheme } from './lib/theme';
import { registerPWA, startUpdateChecks } from './lib/install';
import { useActiveRoom } from './features/room/session';
import { installGlobalHandlers } from './lib/telemetry';
import { AppShell } from './layout/AppShell';
import { Toasts } from './ui';
import { OverviewPage } from './pages/OverviewPage';
import { DrivePage } from './pages/DrivePage';
import { RoomsPage } from './pages/RoomsPage';

// Pages load when they're opened: the room pulls in hls.js and the WebRTC stack, and most visits
// never need setup, sign-in, Settings, Team or Logs. Overview, Drive and Rooms load with the app.
const RoomPage = lazy(() => import('./pages/RoomPage').then((m) => ({ default: m.RoomPage })));
const TeamPage = lazy(() => import('./pages/TeamPage').then((m) => ({ default: m.TeamPage })));
const SettingsPage = lazy(() => import('./pages/SettingsPage').then((m) => ({ default: m.SettingsPage })));
const LogsPage = lazy(() => import('./pages/LogsPage').then((m) => ({ default: m.LogsPage })));
const WaitingPage = lazy(() => import('./pages/WaitingPage').then((m) => ({ default: m.WaitingPage })));
const auth = () => import('./pages/AuthPages');
const LoginPage = lazy(() => auth().then((m) => ({ default: m.LoginPage })));
const SignupPage = lazy(() => auth().then((m) => ({ default: m.SignupPage })));
const ForgotPage = lazy(() => auth().then((m) => ({ default: m.ForgotPage })));
const ResetPage = lazy(() => auth().then((m) => ({ default: m.ResetPage })));
const VerifyPage = lazy(() => auth().then((m) => ({ default: m.VerifyPage })));
const SignInLinkPage = lazy(() => auth().then((m) => ({ default: m.SignInLinkPage })));
const InvitePage = lazy(() => auth().then((m) => ({ default: m.InvitePage })));
const JoinCodePage = lazy(() => auth().then((m) => ({ default: m.JoinCodePage })));
const setup = () => import('./pages/SetupPage');
const SetupPage = lazy(() => setup().then((m) => ({ default: m.SetupPage })));
const SetupRemotePage = lazy(() => setup().then((m) => ({ default: m.SetupRemotePage })));
const ServerDownPage = lazy(() => setup().then((m) => ({ default: m.ServerDownPage })));

applyTheme(savedTheme());
applyMode(savedMode());
installGlobalHandlers();
registerPWA(!!window.VBNative);
startUpdateChecks(() => !!useActiveRoom.getState().roomId);

function App() {
  const { load, ready, config, configError, user, me } = useSession();
  const nav = useNavigate();
  const loc = useLocation();
  const lang = useLang((s) => s.lang);

  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);
  useEffect(() => {
    const onUnauthorized = () => {
      useSession.setState({ user: null, me: null });
      nav('/login');
    };
    window.addEventListener('vb:unauthorized', onUnauthorized);
    return () => window.removeEventListener('vb:unauthorized', onUnauthorized);
  }, [nav]);

  if (!ready) return null;
  if (!config) return <ServerDownPage error={configError} />;

  // First run: only the Mac that runs the server can set it up.
  if (!config.setupComplete) {
    return (
      <Routes>
        <Route path="*" element={config.local ? <SetupPage /> : <SetupRemotePage />} />
      </Routes>
    );
  }

  const shared = (
    <>
      <Route path="/verify/:token" element={<VerifyPage />} />
      <Route path="/signin/:token" element={<SignInLinkPage />} />
      <Route path="/invite/:token" element={<InvitePage />} />
      <Route path="/join/:code" element={<JoinCodePage />} />
      <Route path="/reset/:token" element={<ResetPage />} />
    </>
  );

  if (!user) {
    return (
      <Routes>
        {shared}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/forgot" element={<ForgotPage />} />
        <Route path="*" element={<Navigate to="/login" replace state={{ from: loc.pathname }} />} />
      </Routes>
    );
  }

  if (!me?.member) {
    return (
      <Routes>
        {shared}
        <Route path="*" element={<WaitingPage />} />
      </Routes>
    );
  }

  return (
    <Routes>
      {shared}
      <Route element={<AppShell />}>
        <Route index element={<OverviewPage />} />
        <Route path="/drive" element={<DrivePage />} />
        <Route path="/rooms" element={<RoomsPage />} />
        <Route
          path="/room/:id"
          element={
            <Suspense fallback={null}>
              <RoomPage />
            </Suspense>
          }
        />
        <Route path="/team" element={<TeamPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        {user.host && <Route path="/logs" element={<LogsPage />} />}
      </Route>
      <Route path="/login" element={<Navigate to={(loc.state as { from?: string })?.from ?? '/'} replace />} />
      <Route path="/signup" element={<Navigate to="/" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <BrowserRouter>
      <Suspense fallback={null}>
        <App />
      </Suspense>
      <Toasts />
    </BrowserRouter>
  </StrictMode>,
);
